import { LANGUAGE_PATHS } from './brand.ts';

/**
 * The blog contract shared by the Worker (worker/), the app and the build.
 * Articles are written in Hiquill; the Worker reads them with the secret API
 * key and serves this site a small, cached feed, so the browser never talks to
 * Hiquill and the page keeps its "only our own origin" security policy.
 * Plain constants only, like brand.ts.
 */
export const BLOG_ROUTES = {
  /** The cached feed for the home page: `?locale=en|tr`. */
  feed: '/api/blog/posts',
  /** Article pictures, proxied by the Worker: `/api/blog/media/<id>`. */
  media: '/api/blog/media/',
  /** The blog itself, served from Hiquill's edge by the Worker (worker/blog/proxy.ts). */
  home: '/blog',
  /** Hiquill's sitemap of the blog, through the same proxy; the site's sitemap index lists it. */
  sitemap: '/blog/sitemap.xml',
  /** Hiquill → Publishing → Integrations → Webhook points here. */
  webhook: '/api/hiquill/webhook',
} as const;

/** Articles shown on the home page. */
export const BLOG_POST_LIMIT = 6;

export type BlogLocale = keyof typeof LANGUAGE_PATHS;
export const BLOG_LOCALES = Object.keys(LANGUAGE_PATHS) as BlogLocale[];

export const isBlogLocale = (value: unknown): value is BlogLocale => BLOG_LOCALES.includes(value as BlogLocale);

export interface BlogImage {
  /** Same-origin address (served by the Worker). */
  readonly src: string;
  readonly alt: string;
  readonly width: number | null;
  readonly height: number | null;
}

export interface BlogPost {
  readonly slug: string;
  readonly title: string;
  readonly excerpt: string;
  /** BCP-47 base tag of the article, e.g. `en`. */
  readonly locale: string;
  /** Absolute http(s) address of the article on the blog. */
  readonly url: string;
  /** ISO 8601. */
  readonly publishedAt: string;
  readonly cover: BlogImage | null;
  readonly author: { readonly name: string; readonly avatar: string | null } | null;
  readonly topic: string | null;
}

export interface BlogFeed {
  readonly posts: readonly BlogPost[];
  /** The blog's home page, or null when Hiquill could not be asked yet. */
  readonly blogUrl: string | null;
  /** True when the page language has no articles yet and these are in other languages. */
  readonly fallback: boolean;
}
